import React from 'react';
interface LatexRendererProps {
  content: string;
  className?: string;
}
/**
 * Simple math renderer without external dependencies
 * Converts common math notation to HTML/CSS
 */
export function LatexRenderer({
  content,
  className = ''
}: LatexRendererProps) {
  if (!content) return null;
  const renderMath = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    // Pattern: $\frac{numerator}{denominator}$
    const fractionRegex = /\$\\frac\{([^}]+)\}\{([^}]+)\}\$/g;
    // Pattern: $cm^2$ or $m^3$
    const superscriptRegex = /\$([a-zA-Z]+)\^(\d+)\$/g;
    // Pattern: $\times$ or $\cdot$
    const multiplyRegex = /\$(\\times|\\cdot)\$/g;
    // Pattern: simple inline math $...$
    const inlineMathRegex = /\$([^$]+)\$/g;
    let lastIndex = 0;
    const matches: Array<{
      index: number;
      length: number;
      element: React.ReactNode;
    }> = [];
    // Find all fractions
    let match;
    while ((match = fractionRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        element: <span key={`frac-${match.index}`} className="inline-flex flex-col items-center mx-1 align-middle" style={{
          verticalAlign: 'middle'
        }} data-id="element-1201">
            <span className="text-sm border-b border-slate-900 px-1" data-id="element-1202">
              {match[1]}
            </span>
            <span className="text-sm px-1" data-id="element-1203">{match[2]}</span>
          </span>
      });
    }
    // Find all superscripts
    superscriptRegex.lastIndex = 0;
    while ((match = superscriptRegex.exec(text)) !== null) {
      // Check if this overlaps with a fraction
      const overlaps = matches.some(m => match.index >= m.index && match.index < m.index + m.length);
      if (!overlaps) {
        matches.push({
          index: match.index,
          length: match[0].length,
          element: <span key={`sup-${match.index}`} data-id="element-1204">
              {match[1]}
              <sup className="text-xs" data-id="element-1205">{match[2]}</sup>
            </span>
        });
      }
    }
    // Find all multiply symbols
    multiplyRegex.lastIndex = 0;
    while ((match = multiplyRegex.exec(text)) !== null) {
      const overlaps = matches.some(m => match.index >= m.index && match.index < m.index + m.length);
      if (!overlaps) {
        const symbol = match[1] === '\\times' ? '×' : '·';
        matches.push({
          index: match.index,
          length: match[0].length,
          element: <span key={`mult-${match.index}`} data-id="element-1206">{symbol}</span>
        });
      }
    }
    // Sort matches by index
    matches.sort((a, b) => a.index - b.index);
    // Build the result
    let currentPos = 0;
    matches.forEach(({
      index,
      length,
      element
    }) => {
      // Add text before this match
      if (index > currentPos) {
        parts.push(text.substring(currentPos, index));
      }
      // Add the rendered element
      parts.push(element);
      currentPos = index + length;
    });
    // Add remaining text
    if (currentPos < text.length) {
      parts.push(text.substring(currentPos));
    }
    return parts.length > 0 ? <>{parts}</> : text;
  };
  return <span className={className} data-id="element-1207">{renderMath(content)}</span>;
}